import React from 'react';
import './MiniCoachingPlanDialog.styl';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import CogIcon from 'components/pure/utils/icon/CogIcon';
import CoachingPlan from 'records/CoachingPlan';
import MeetingType from 'records/MeetingType';
import {List, Map} from 'immutable';
import SkillActions from 'actions/SkillActions';
import AuthStore from 'stores/AuthStore';
import moment from 'moment';
import connectToStores from 'alt-utils/lib/connectToStores';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import SkillStore from 'stores/SkillStore';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import SelectField from 'components/pure/utils/form/SelectField';
import CoachingContentDetail, {nonSkillRelated} from 'records/CoachingContentDetail';
import Complete from 'components/coaching/wizard/steps/Complete';
import pick from 'lodash/pick';
import {UserSelector} from 'components/pure/utils/form/UserSelector';
import UserStore from 'stores/UserStore';
import UserActions from 'actions/UserActions';
import CoachingParticipant from 'records/CoachingParticipant';
import ToastActions from '@store/toast/ToastActions';
import TimeField from 'components/pure/utils/form/TimeField';
import confirm from 'utils/confirm';
import DirectReportsSource from 'sources/coaching/DirectReportsSource';
import ScorecardActions from 'actions/scorecards/ScorecardActions';
import pluralize from 'pluralize';
import ReviewTypeDropdown from 'components/dashboard/common/ReviewTypeDropdown';
import SelectizeLookingDropdownArrow from 'components/utils/SelectizeLookingDropdownArrow';
import isNil from 'lodash/isNil';
import clamp from 'lodash/clamp';
import Time from 'records/Time';
import MultiSelect from '@components/multi-select';
import {FindTimeContainer} from '@components/calendar/FindTimeContainer';
import Button from '@material-ui/core/Button';
import {ensureReviewMetadata} from 'components/StatsPage2.utils';
import DialogContent from '@material-ui/core/DialogContent';

export const defaultRecurrenceCount = 4;
export const defaultTimeInMinutes = 15;

const TOOLTIPS = {
  coach: 'Who will perform the coaching.',
  rep: `The rep who is receiving the coaching.
    They will receive an email invite to the coaching plan.`,
  reviewType: `Leave as "Call Focused Session" if you will be discussing
    calls during the meeting. The rep will be asked to add calls in advance
    for discussion, and the coach will have the opportunity to do so also.`,
  skills: `The skill that this coaching plan will be focused on.`,
  date: `Date and time for the coaching meetings. The rep and coach will receive
    a calendar invite for this time.`,
  schedule: `How many meetings should occur at the specified interval. Invites will be sent for
  the total meeting amount chosen, but the coach can end the plan at any time before that.`,
};

const SCHEDULING_RANGE = [8, 18];

/**
 * Clamp a time's hour to the scheduling range above.
 *
 * @param time {Time} the time
 * @return {Time} the original time, with its hour clamped to the range.
 */
function clampTimeToRange(time) {
  if (isNil(time.hour)) {
    return time;
  }
  const clamped = clamp(time.hour, ...SCHEDULING_RANGE);
  return clamped === time.hour
    ? time
    : new Time({
        hour: clamped,
        ...time,
      });
}

@connectToStores
export default class MiniCoachingPlanDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isComplete: false,
      showErrors: false,
      submitting: false,
    };

    this.wasError = this.wasError.bind(this);
    this.isLoading = this.isLoading.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCreate = this.onCreate.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.getErrors = this.getErrors.bind(this);
    this.setRep = this.setRep.bind(this);
    this.setCoach = this.setCoach.bind(this);
  }

  static getStores() {
    return [AuthStore, CoachingReviewTypeStore, SkillStore, UserStore];
  }

  static getPropsFromStores(props) {
    // if rep and/or coach is specified make sure its present in our list
    const users = UserStore.getState()
      .users.set(props.rep?.id, props.rep)
      .set(props.coach?.id, props.coach)
      // If either of those was undefined, make sure we delete that entry;
      .delete(undefined);

    const [reviewTypesLoaded, reviewTypes] = typesAndCategories(CoachingReviewTypeStore);

    return {
      users,
      sessionUser: AuthStore.getState().user,
      ...pick(SkillStore.getState(), [
        'skillsByTeam',
        'areSkillsFetchingByTeam',
        'wasErrorFetchingSkillsByTeam',
      ]),
      types: reviewTypes,
      typesLoading: !reviewTypesLoaded,
    };
  }

  async componentDidMount() {
    const {onChange, coach, rep, model} = this.props;
    const coachId = coach && coach.id;

    const {types} = this.props;
    const defaultType = types.find(t => t.isDefault);

    const plan = model || initialModel(coach, defaultType);
    const repId = (rep && rep.id) || (plan.recipient && plan.recipient.id);

    if (!coachId || !repId) {
      // If we don't have coach and rep specified, fetch users for our dropdown
      // use the role based api since that populates isTeamManager, which
      // is fed into the logic in `canCoach`
      UserActions.fetchAll({roles: ['admin', 'manager', 'user']});
    }

    // reps from Calls page (via python) don't include needed departmentId
    await (rep && !rep.departmentId
      ? UserActions.fetchAll({ids: List([repId])})
      : Promise.resolve());

    ensureReviewMetadata();
    onChange(plan);
    this.setRep(repId, plan);
  }

  setCoach(coachId) {
    const {model, onChange} = this.props;
    if (coachId == null && model) {
      return onChange(model.delete('coach'));
    }

    const coach = this.props.users.get(coachId);
    if (!coach) return console.error(`Couldn't find coach in user store`);

    onChange(
      model.set(
        'coach',
        new CoachingParticipant({
          id: coach.id,
          info: coach.toJS(),
        })
      )
    );
  }

  async setRep(repId, plan) {
    const model = this.props.model || plan;
    const onChange = this.props.onChange;
    if (repId == null && model) {
      return onChange(model.delete('recipient').set('contentDetails', List()));
    }

    const {skillToCoach: skillName, users} = this.props;
    const rep = users.get(repId);
    if (!rep) return console.error(`Couldn't find rep in user store`);

    const updatedModel = onChange(
      model.set(
        'recipient',
        new CoachingParticipant({
          id: rep.id,
          info: rep.toJS(),
        })
      )
    );

    const [skills] = await SkillActions.fetchAllByTeam({team_id: rep.teamId});
    let challengedBy = undefined;
    if (!skillName) {
      challengedBy = (await DirectReportsSource.getSkillBreakdown([repId])).find(
        cb => cb.userId === repId
      )?.worstSkill?.skillName;
    }

    const previousSkills = model.contentDetails.map(d => d.skillName);

    // 1. If we had skills set manually use those
    // 2. If there was a specific skill passed in (recommendation) - use that
    // 3. Fall back to the rep's challenged by skill
    // 4. if ALL OF THAT FAILS, we map over the empty list

    const skillsToCoach = !previousSkills.isEmpty()
      ? previousSkills.map(ps => skills.find(s => s.skill === ps))
      : skillName
      ? List([skills.find(s => s.skill === skillName)])
      : challengedBy
      ? List([skills.find(s => s.skill === challengedBy)])
      : List();

    const cds =
      skillsToCoach &&
      skillsToCoach
        .filter(x => x)
        .map(
          skill =>
            new CoachingContentDetail({
              id: undefined,
              skillId: skill.id,
              skillName: skill.skill,
              skillBaseline: 0,
              skillGoal: 75,
              isSkillBased: true,
            })
        );

    onChange(
      updatedModel.set(
        'contentDetails',
        // If we have a skill specified in props
        // and it exists in the team's set of skills, set that
        cds
      )
    );
  }

  wasError() {
    const {typesError, wasErrorFetchingSkillsByTeam} = this.props;
    return typesError || wasErrorFetchingSkillsByTeam;
  }

  isLoading() {
    const {model: coachingPlan, typesLoading, skillsByTeam, types} = this.props;

    return (
      typesLoading ||
      skillsByTeam === undefined ||
      types === undefined ||
      coachingPlan === undefined
    );
  }

  onUpdateContentDetails(skills) {
    const {onChange, model} = this.props;
    return detailIds => {
      const selectedSkills = detailIds.map(detailId => skills.find(s => s.id === detailId));

      if (detailIds.size !== selectedSkills.size) {
        console.error("Couldn't find all skills selected. Cannot continue");
        return;
      }

      const contentDetails = selectedSkills.map(
        skill =>
          new CoachingContentDetail({
            id: undefined,
            skillId: skill.id,
            skillName: skill.skill,
            skillBaseline: 0,
            skillGoal: 75,
            isSkillBased: true,
          })
      );

      onChange(model.set('contentDetails', contentDetails));
    };
  }

  onSubmit() {
    this.setState({showErrors: true});

    const errors = this.getErrors();

    if (errors.size === 0) {
      this.onCreate().catch(console.error);
    }
  }

  async getScorecardId(skillIds) {
    const {model: coachingPlan} = this.props;
    const departmentId = coachingPlan.recipient.info.departmentId;

    // if we dont have relevant skills, dont fetch scorecards
    if (skillIds.isEmpty()) return null;

    const cards = await ScorecardActions.fetchScorecardsForDepartment(departmentId, true, {
      repId: coachingPlan.recipient.info.userId,
      skillIds: skillIds.toArray(),
    });
    const skillFilteredScorecards = cards.filter(
      sc => sc.getQuestionSkillIds().intersect(skillIds).size > 0
    );

    // Try to find a card which uses both skills
    const idealCard = skillFilteredScorecards.find(sc =>
      sc.getQuestionSkillIds().isSuperset(skillIds)
    );

    if (idealCard) return idealCard.id;

    // otherwise, settle for whats there
    return skillFilteredScorecards && !skillFilteredScorecards.isEmpty()
      ? skillFilteredScorecards.first().id
      : null;
  }

  async onCreate() {
    this.setState({submitting: true});
    const {model: coachingPlan, types} = this.props;
    const selectedReviewType = types.find(t => t.id === coachingPlan.reviewTypeIds.first());
    const skillIds = coachingPlan.contentDetails.map(d => d.skillId).filter(skillId => skillId);
    const scorecardId = await this.getScorecardId(skillIds);

    const includesCalls = selectedReviewType && selectedReviewType.includesCalls;
    const plan = coachingPlan
      .set('scorecardId', scorecardId)
      .setIn(['coach', 'prepNumberOfCalls'], includesCalls ? 1 : 0)
      .setIn(['recipient', 'prepNumberOfCalls'], includesCalls ? 1 : 0)
      .setIn(['recipient', 'prepShouldComment'], includesCalls)
      .setIn(
        ['defaultMeetingType', 'locationDetail'],
        `${coachingPlan.coach.info.getFullName()}'s desk`
      )
      .set(
        // If the plan has no skills, then add the placeholder
        'contentDetails',
        skillIds.isEmpty() ? List([nonSkillRelated]) : coachingPlan.contentDetails
      );

    try {
      await this.props.createCoachingPlan(
        plan
          .set('objective', plan.getDefaultCoachingPlanObjective())
          .set('name', plan.getDefaultCoachingPlanShortName(moment()))
      );

      this.setState({isComplete: true, submitting: false});
    } catch (e) {
      this.setState({submitting: false});
      console.error(e);
      ToastActions.addToast({
        type: 'error',
        message: 'There was a problem creating this plan',
      });
    }
  }

  getErrors() {
    const {model: coachingPlan} = this.props;

    let errors = Map();

    if (!coachingPlan.coach) {
      errors = errors.set('coach', 'Please select a coach');
    }

    if (!coachingPlan.recipient) {
      errors = errors.set('recipient', 'Please select a rep to coach');
    }

    if (coachingPlan.reviewTypeIds.isEmpty()) {
      errors = errors.set('reviewTypeIds', 'Please select a meeting type');
    }

    return errors;
  }

  onCancel() {
    confirm('Are you sure you want to cancel creating this coaching plan?').then(() => {
      this.props.onClose();
    });
  }

  render() {
    const {
      switchToAdvanced,
      open,
      onClose,
      onCompleted,
      types,
      skillsByTeam,
      users,
      sessionUser,
      forceRenderRepSelect,
      model: coachingPlan,
      onChange,
    } = this.props;
    const {isComplete, showErrors, submitting} = this.state;
    if (!coachingPlan) {
      return null;
    }

    const rep = coachingPlan.recipient && coachingPlan.recipient.info;
    const coach = coachingPlan.coach && coachingPlan.coach.info;

    const skills = rep ? skillsByTeam.get(rep.teamId, List()) : List();

    const wasError = this.wasError();
    const isLoading = this.isLoading();

    const [errors, skillOptions, typeOptions] = isLoading
      ? [List(), [], List()]
      : [
          this.getErrors(),
          skills
            .map(skill => ({
              title: skill.skill,
              value: skill.id,
            }))
            .toJS(),
          types.toList(),
        ];

    // Only hour choice allowed in short wizard, so hard code minutes to zero.
    const desiredMeetingTime = `${coachingPlan.schedule.time.hour}:00`.padStart(5, '0');

    const findTimeChoiceMade = chosenFindTime => {
      onChange(
        coachingPlan
          .setIn(
            ['schedule', 'time'],
            clampTimeToRange(
              new Time({
                hour: chosenFindTime.hour,
                minute: chosenFindTime.minutes,
              })
            )
          )
          .setIn(
            ['schedule', 'daysOfWeek'],
            coachingPlan.schedule.daysOfWeek.includes(chosenFindTime.day)
              ? coachingPlan.schedule.daysOfWeek
              : List([chosenFindTime.day])
          )
      );
    };

    return (
      <Dialog className="mini-plan-creation-dialog" open={open}>
        <ClosableDialogTitle onClose={isComplete ? onCompleted : this.onCancel}>
          Create Coaching Plan
        </ClosableDialogTitle>
        <DialogContent>
          {isComplete ? (
            <Complete model={coachingPlan} sessionUser={sessionUser} />
          ) : (
            <div className="dialog-wrapper">
              <section className="header">
                <div>
                  Create a new Coaching Plan
                  {this.props.rep && (
                    <span>
                      {' '}
                      between{' '}
                      {!this.props.coach ? (
                        <strong>you</strong>
                      ) : (
                        <strong>{this.props.coach.getFullName()}</strong>
                      )}{' '}
                      and <strong>{this.props.rep && this.props.rep.getFullName()}</strong>
                    </span>
                  )}
                  . Choose options below for the Coaching Plan and Coach will set up the agenda for
                  the plan, and send calendar reminders. (Switch to advanced settings if you'd like
                  to fine tune the plan.)
                </div>
              </section>
              <section className="form">
                {wasError && <p>There was an error loading skills or review types.</p>}
                {isLoading && <LoadingOverlay />}
                {!wasError && !isLoading && (
                  <section className="form-body">
                    <FieldContainer
                      labelType="div"
                      label="Coach"
                      tooltip={TOOLTIPS.coach}
                      error={showErrors && errors.get('coach')}>
                      <UserSelector
                        users={users.filter(u => u.id !== rep?.id && u.canCoach())}
                        value={coach && coach.id}
                        sessionUser={sessionUser}
                        onChange={this.setCoach}
                      />
                    </FieldContainer>
                    {(forceRenderRepSelect || !this.props.rep) && (
                      <FieldContainer
                        labelType="div"
                        label="Rep"
                        tooltip={TOOLTIPS.rep}
                        error={showErrors && errors.get('recipient')}>
                        <UserSelector
                          users={users.delete(coach?.id)}
                          value={rep && rep.id}
                          sessionUser={sessionUser}
                          onChange={this.setRep}
                        />
                      </FieldContainer>
                    )}
                    <FieldContainer
                      labelType="div"
                      tooltip={TOOLTIPS.reviewType}
                      label="Coaching Meeting Type"
                      error={showErrors && errors.get('reviewTypeIds')}>
                      <ReviewTypeDropdown
                        value={coachingPlan.reviewTypeIds.first()}
                        coachingReviewTypes={typeOptions}
                        onSelect={type =>
                          onChange(coachingPlan.set('reviewTypeIds', List([type.id])))
                        }
                        width={295}
                        dropdownHeight={150}
                        arrow={(open, onShowDropdown) => (
                          <SelectizeLookingDropdownArrow
                            open={open}
                            onShowDropdown={onShowDropdown}
                          />
                        )}
                      />
                    </FieldContainer>
                    <FieldContainer labelType="div" tooltip={TOOLTIPS.skills} label="Skill(s)">
                      <MultiSelect
                        className="smaller-multi-select"
                        readOnly={coachingPlan.contentDetails.size >= 2}
                        options={
                          coachingPlan.contentDetails.size >= 2
                            ? skillOptions.filter(x =>
                                coachingPlan.contentDetails.map(cd => cd.skillId).includes(x.value)
                              )
                            : skillOptions
                        }
                        onChange={chosenSkills =>
                          this.onUpdateContentDetails(skills)(List(chosenSkills))
                        }
                        value={coachingPlan.contentDetails.map(cd => cd.skillId).toJS()}
                        noOptionsText={
                          coachingPlan.contentDetails.size >= 2
                            ? 'Maximum # of skills selected'
                            : rep
                            ? 'No skills available'
                            : 'Select a rep to load available skills'
                        }
                      />
                    </FieldContainer>
                    <div className="schedule">
                      <FieldContainer labelType="div" tooltip={TOOLTIPS.date} label="Meet on">
                        <SelectField
                          value={coachingPlan.schedule.daysOfWeek.get(0)}
                          options={daysOfWeek.map(d => ({label: d, value: d}))}
                          tether
                          hideResetButton
                          onChange={value =>
                            onChange(coachingPlan.setIn(['schedule', 'daysOfWeek'], List([value])))
                          }
                          className="meet-on-selector"
                        />
                      </FieldContainer>
                      <FieldContainer labelType="div" label="" className="time-field-container">
                        <TimeField
                          renderMinutes={false}
                          timeRange={SCHEDULING_RANGE}
                          value={clampTimeToRange(coachingPlan.schedule.time)}
                          onChange={val => onChange(coachingPlan.setIn(['schedule', 'time'], val))}
                        />
                      </FieldContainer>
                      <div className="find-time-container">
                        <FindTimeContainer
                          onlyOnTheHour={true}
                          coachUserId={coachingPlan.coach?.id}
                          recipientUserId={coachingPlan.recipient?.id}
                          recipientFirstName={coachingPlan.recipient?.info?.firstName}
                          meetingMinutes={coachingPlan.schedule.durationInMinutes}
                          searchWeeks={
                            coachingPlan.schedule.weeksBetween *
                            coachingPlan.schedule.recurrenceCount
                          }
                          dayOfWeek={
                            coachingPlan.schedule.daysOfWeek
                              .first()
                              .toUpperCase() /* Only ever one day chosen in short wizard, so don't need to worry about sorting */
                          }
                          desiredTime={desiredMeetingTime}
                          findTimeChoiceMade={findTimeChoiceMade}
                        />
                      </div>
                    </div>
                    <FieldContainer
                      labelType="div"
                      label="Schedule"
                      tooltip={TOOLTIPS.schedule}
                      className="every-field-container">
                      <div className="selections">
                        <span>Every</span>
                        <SelectField
                          value={coachingPlan.schedule.weeksBetween}
                          options={every.map(d => ({
                            label: d + '',
                            value: d,
                          }))}
                          tether
                          hideResetButton
                          onChange={value => {
                            onChange(coachingPlan.setIn(['schedule', 'weeksBetween'], value));
                          }}
                          className="selector"
                        />
                        <span className="weeks">
                          {pluralize('week', coachingPlan.schedule.weeksBetween)} for a total of
                        </span>
                        <SelectField
                          value={coachingPlan.schedule.recurrenceCount}
                          options={total.map(d => ({
                            label: d + '',
                            value: d,
                          }))}
                          tether
                          hideResetButton
                          onChange={value => {
                            onChange(coachingPlan.setIn(['schedule', 'recurrenceCount'], value));
                          }}
                          className="selector"
                        />
                        <span> {pluralize('meeting', coachingPlan.schedule.recurrenceCount)}</span>
                      </div>
                    </FieldContainer>
                  </section>
                )}
              </section>
            </div>
          )}
        </DialogContent>
        <DialogActions className="dialog-actions">
          {isComplete ? (
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              onClick={() => {
                onChange();
                onCompleted();
              }}>
              Great, let's do this
            </Button>
          ) : (
            <>
              <Button size="medium" variant="text" className="advanced" onClick={switchToAdvanced}>
                <CogIcon />
                Switch to advanced settings
              </Button>
              <div className="create-or-cancel">
                <Button
                  size="medium"
                  variant="outlined"
                  className="cancel-button"
                  onClick={() => {
                    onChange();
                    onClose();
                  }}>
                  Cancel
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  color="primary"
                  className="create-button"
                  onClick={this.onSubmit}
                  disabled={submitting || (showErrors && errors.size > 0)}>
                  Create
                </Button>
              </div>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const every = [1, 2, 3, 4];
const total = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function initialModel(coach, defaultType) {
  const oneHourFromNow = moment().add(1, 'hours');
  return new CoachingPlan({
    coach: coach && {
      id: coach.id,
      info: coach.toJS(),
    },
    defaultMeetingType: new MeetingType({
      meetingType: 'PhysicalLocation',
      locationDetail: '',
    }),
    contentDetails: List(),
    schedule: {
      weeksBetween: 1,
      daysOfWeek: [oneHourFromNow.format('dddd')],
      time: {
        hour: oneHourFromNow.format('HH'),
        minute: 0,
      },
      recurrenceCount: defaultRecurrenceCount,
      durationInMinutes: defaultTimeInMinutes,
    },
    reviewTypeIds: defaultType ? List([defaultType.id]) : List(),
  });
}
